import { useMemo, useRef, useState } from "react";
import { useRevealSearchTarget, type SearchFocus } from "@/shared/hooks/useSearchFocus";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { ThemedSegmented } from "@/shared/components/ThemedSegmented";
import { Skeleton } from "antd";
import { Button } from "@/shared/components/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { Select } from "@/shared/components/select";
import { LG_QUERY } from "@/shared/hooks/useMediaQuery";
import { FilePane } from "@/features/patient/records/components/OriginalFileTab";
import { isAbnormalLabStatus } from "@/features/patient/diagnostics/utils/adaptDiagnostics";
import type {
  LabReportRecord as LabResult,
  LabComponentResult as LabTestResult,
} from "@/features/patient/diagnostics/utils/adaptDiagnostics";
import { useImagingStudies } from "@/portals/patient/context/ImagingStudiesContext";
import type { ImagingSeries } from "@/portals/patient/api/imagingStudiesApi";
import { ImagingStudyList } from "@/features/patient/diagnostics/components/imaging/ImagingStudyList";
import { openSeriesViewer, type DiagnosticsLocationState } from "@/features/patient/diagnostics/components/imaging/imagingNavigation";
import { cn } from "@/shared/lib/utils";
import { FoundInRecordsTag } from "@/shared/components/FoundInRecords";
import { GoldenSources, GoldenTag } from "@/features/empi/GoldenRecord";
import { listCardClass } from "@/shared/lib/cardStyles";

interface DiagnosticsImagingSectionProps {
  labResults: LabResult[];
  isLoading?: boolean;
  /** A header search result to open here (useSearchFocus.ts). */
  focus?: SearchFocus;
}

type Tab = "labs" | "imaging";

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const categoryLabels: Record<string, string> = {
  blood: "Blood Test", urine: "Urine Test", microbiology: "Microbiology",
  pathology: "Pathology", genetic: "Genetic Test", other: "Other",
};

// Design reference: the status word (Low/High/Normal) always renders in the
// same navy as the component name — only the big value and its dot switch to
// coral (abnormal) or blue (normal). A colored label was the old behavior;
// keeping it navy for every status matches the mockup exactly.
function getResultStatusMeta(status: LabTestResult["status"]) {
  switch (status) {
    case "low":
    case "high":
      return { label: status === "low" ? "Low" : "High", valueClass: "text-[#E4002B]/50", dotClass: "bg-[#E4002B]/50" };
    case "critical_low":
    case "critical_high":
      return { label: "Critical", valueClass: "text-[#DC2626]", dotClass: "bg-[#DC2626]" };
    case "abnormal":
      return { label: "Abnormal", valueClass: "text-[#E4002B]/50", dotClass: "bg-[#E4002B]/50" };
    case "unknown":
      return { label: "Not Assessed", valueClass: "text-[#9CA3AF]", dotClass: "bg-[#9CA3AF]" };
    default:
      return { label: "Normal", valueClass: "text-[#111827]", dotClass: "bg-[#00A7F2]" };
  }
}

const greyPillClass = "rounded-md bg-[#F3F4F6] px-2 py-0.5 text-[10px] font-semibold text-[#6B7280]";
// !min-w-0 is load-bearing: without it, a long test/study name below has no
// upper bound and forces this button (and the single-column grid track it
// sits in) wider than its 340px sidebar column, overflowing into — and
// visually painting over — the detail panel next to it.
// !items-stretch overrides antd's own Button default of `align-items: center`
// on this flex-col container. Without it, each stacked row (title, pills,
// date) sizes to its own shrink-to-fit content width and gets CENTERED
// instead of stretched to the button's width — a row wider than the button
// then overflows equally on both sides, floating outside the card entirely.
const rowBaseClass = "!flex !h-auto !w-full !min-w-0 !flex-col !items-stretch !gap-1.5 !rounded-2xl !px-3.5 !py-3 !text-left !shadow-sm";

function StatCard({
  icon, label, value, onClick,
}: {
  icon: string; label: string; value: number; onClick: () => void;
}) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      className="!flex !h-auto !min-h-[110px] !flex-col !items-stretch !justify-between !rounded-3xl !border-[#E5E7EB] !bg-white !px-6 !py-5 !text-left !shadow-sm hover:!-translate-y-0.5 hover:!border-[#E5E7EB] hover:!bg-white hover:!shadow-lg"
    >
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E4002B]/50">
          <Icon icon={icon} className="h-4 w-4 !text-white" />
        </div>
        <p className="text-sm font-semibold text-[#111827]">{label}</p>
      </div>
      <p className="text-right text-3xl font-bold text-[#111827]">{value}</p>
    </Button>
  );
}

function EmptyListState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#E5E7EB] bg-white py-10 text-center">
      <Icon icon={icon} className="h-10 w-10 text-[#9CA3AF]" />
      <p className="mt-3 text-xs font-semibold text-[#9CA3AF]">{message}</p>
    </div>
  );
}

function LabRow({
  result, isSelected, onSelect,
}: {
  result: LabResult; isSelected: boolean; onSelect: () => void;
}) {
  const abnormalCount = result.results.filter(r => isAbnormalLabStatus(r.status)).length;
  // Every component genuinely unassessed (no interpretation, nothing to
  // compare against a range) — distinct from "checked and found normal".
  const allUnassessed = result.results.length > 0 && result.results.every(r => r.status === "unknown");
  // No values at all: nothing was measured here, so never "Normal".
  const noValues = result.results.length === 0;

  return (
    <Button
      variant="outline"
      onClick={onSelect}
      className={cn(rowBaseClass, "!max-w-full overflow-hidden !whitespace-normal", listCardClass(isSelected))}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#E4002B]/50">
            <Icon icon="lucide:test-tube" className="h-4 w-4 !text-white" />
          </div>
          <p className="min-w-0 truncate text-sm font-extrabold text-[#0A2947]" title={result.testName}>{result.testName}</p>
        </div>
        <span className={cn(
          "shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold",
          abnormalCount > 0
            ? "bg-[#E4002B]/50 text-white"
            : allUnassessed || noValues ? "bg-[#F3F4F6] text-[#6B7280]" : "bg-[#DCFCE7] text-[#16A34A]"
        )}>
          {abnormalCount > 0 ? `${abnormalCount} Abnormal` : noValues ? "No values" : allUnassessed ? "Not Assessed" : "Normal"}
        </span>
      </div>
      {/* One line: what kind of test, its status only when it isn't final, and when. */}
      <div className="flex flex-wrap items-center gap-1.5 pl-[42px]" data-testid="lab-row-tags">
        <span className={greyPillClass}>{categoryLabels[result.category]}</span>
        {result.status !== "final" && <span className={cn(greyPillClass, "capitalize")}>{result.status}</span>}
        <span className="text-xs text-[#6B7280]">{result.collectionDate ? formatDate(result.collectionDate) : "Date not recorded"}</span>
        {result.duplicates && <FoundInRecordsTag duplicates={result.duplicates} />}
      </div>
    </Button>
  );
}

/** Flag pill per result: tinted for out-of-range, grey when never assessed. */
const FLAG_TONE: Record<string, string> = {
  low: "bg-[#E0F2FE] text-[#0369A1]",
  high: "bg-[#FDECEF] text-[#C2002A]",
  abnormal: "bg-[#FDECEF] text-[#C2002A]",
  critical_low: "bg-[#DC2626] text-white",
  critical_high: "bg-[#DC2626] text-white",
  unknown: "bg-[#F3F4F6] text-[#6B7280]",
  normal: "bg-[#ECFDF3] text-[#027A48]",
};

/** "From 2 sources": every distinct source behind this report's results, counted once. */
function reportSources(results: LabResult["results"]) {
  const golden = results.find((r) => r.golden)?.golden ?? null;
  const sources = new Set(results.flatMap((r) => (r.golden?.sources ?? []).map((s) => s.uploadId ?? s.recordId)));
  return { golden, count: sources.size };
}

/** One outline "info" icon for "where this came from", the same in the header text and the table. */
function SourceInfoIcon({ className }: { className?: string }) {
  return <Icon icon="lucide:info" aria-hidden className={cn("h-[18px] w-[18px] shrink-0", className)} />;
}

function ResultRow({ r }: { r: LabResult["results"][number] }) {
  const [open, setOpen] = useState(false);
  const meta = getResultStatusMeta(r.status);
  const abnormal = isAbnormalLabStatus(r.status);
  const range = r.referenceRange ? (r.unit && /\d/.test(r.referenceRange) ? `${r.referenceRange} ${r.unit}` : r.referenceRange) : "";
  return (
    <>
      <tr data-testid="lab-result-row" className={cn("border-t border-[#F3F4F6] align-middle", abnormal && "bg-[#FFF7F8]")}>
        <td className="px-3 py-2.5">
          <p className="truncate text-sm font-semibold text-[#111827]" title={r.componentName}>{r.componentName}</p>
        </td>
        <td className="truncate px-3 py-2.5 text-sm" title={[r.value, r.unit].filter(Boolean).join(" ")}>
          <span className={cn("font-bold", abnormal ? "text-[#C2002A]" : "text-[#111827]")}>{r.value || "—"}</span>
          {r.unit && r.value && <span className="ml-1 text-xs text-[#6B7280]">{r.unit}</span>}
        </td>
        <td className="break-words px-3 py-2.5 text-xs text-[#6B7280]">{range || "—"}</td>
        <td className="whitespace-nowrap px-3 py-2.5">
          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-bold", FLAG_TONE[r.status] ?? FLAG_TONE.unknown)}>{meta.label}</span>
        </td>
        <td className="px-2 py-2 text-right">
          {r.golden && (
            <button
              type="button"
              aria-label={`Where ${r.componentName} came from`}
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors",
                open ? "bg-[#EEF2F7] text-[#0A2947]" : "text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#0A2947]",
              )}
            >
              <SourceInfoIcon />
            </button>
          )}
        </td>
      </tr>
      {open && r.golden && (
        <tr className="bg-[#F9FAFB]">
          <td colSpan={5} className="px-3 py-2">
            <GoldenTag golden={r.golden} />
            {/* The info icon already asked for the details: shown in full, no second click. */}
            <GoldenSources golden={r.golden} className="mt-1" defaultOpen />
          </td>
        </tr>
      )}
    </>
  );
}

/** A lab report's results: Test / Result / Normal range / Flag, fixed columns,
 *  "Abnormal only" and a per-result source icon. Shared by Diagnostics & Imaging
 *  and the Health Timeline, so a report reads the same in both. */
export function LabResultsTable({ results }: { results: LabResult["results"] }) {
  const [abnormalOnly, setAbnormalOnly] = useState(false);
  const abnormalCount = results.filter((r) => isAbnormalLabStatus(r.status)).length;
  const rows = abnormalOnly ? results.filter((r) => isAbnormalLabStatus(r.status)) : results;
  return (
    results.length === 0 ? (
      <EmptyState
        className="mt-6"
        icon={<Icon icon="lucide:flask-conical" className="h-8 w-8 text-[#9CA3AF]" />}
        title="No result values linked"
        description="This report doesn't have individual component values attached."
      />
    ) : (
      <>
        {/* Red like the "Lab Result" label: the text, and the box when ticked. */}
        {abnormalCount > 0 && (
          <label className="mt-5 flex w-fit cursor-pointer items-center gap-2 text-xs font-semibold text-[#E4002B]">
            <input
              type="checkbox"
              checked={abnormalOnly}
              onChange={(e) => setAbnormalOnly(e.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer accent-[#E4002B]"
            />
            Abnormal only ({abnormalCount})
          </label>
        )}
        <div className="mt-3 overflow-x-auto rounded-2xl border border-[#E5E7EB]">
          {/* Fixed column widths: filtering rows never moves the columns. */}
          <table className="w-full min-w-[520px] table-fixed text-left" data-testid="lab-results-table">
            <colgroup>
              <col className="w-[38%]" />
              <col className="w-[18%]" />
              <col className="w-[22%]" />
              <col className="w-[15%]" />
              <col className="w-12" />
            </colgroup>
            <thead className="bg-[#F9FAFB] text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
              <tr>
                <th className="px-3 py-2">Test</th>
                <th className="px-3 py-2">Result</th>
                <th className="px-3 py-2">Normal range</th>
                <th className="px-3 py-2">Flag</th>
                <th className="px-2 py-2"><span className="sr-only">Source</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <ResultRow key={r.id} r={r} />)}
            </tbody>
          </table>
        </div>
      </>
    )
  );
}

function LabResultDetail({ result }: { result: LabResult }) {
  const { golden, count } = reportSources(result.results);
  const by = result.orderingProvider;
  return (
    <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="text-xs font-extrabold uppercase tracking-wide text-[#E4002B]">Lab Result</span>
        {/* "Final" is the normal state: only a status that needs attention shows. */}
        {result.status !== "final" && (
          <span className="rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-[10px] font-bold capitalize text-[#6B7280]">{result.status}</span>
        )}
        <FoundInRecordsTag duplicates={result.duplicates} />
      </div>
      <h2 className="text-lg font-extrabold text-[#111827]">{result.testName}</h2>
      <p className="mt-1 text-sm text-[#6B7280]" data-testid="lab-result-meta">
        {[formatDate(result.collectionDate), by ? `Ordered by ${by}` : ""].filter(Boolean).join(" · ")}
      </p>
      {golden && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#6B7280]" data-testid="lab-result-sources">
          <GoldenTag golden={golden} />
          {count > 0 && (
            <span className="inline-flex items-center gap-1">
              From {count} {count === 1 ? "source" : "sources"} · select
              <SourceInfoIcon className="h-3.5 w-3.5 text-[#6B7280]" />
              on a result to see where it came from
            </span>
          )}
        </div>
      )}

      <LabResultsTable results={result.results} />

      {(result.diagnosticReportId || result.documentReferenceId) && (
        <div className="mt-6">
          <FilePane
            diagnosticReportId={result.diagnosticReportId || undefined}
            documentReferenceId={result.documentReferenceId || undefined}
          />
        </div>
      )}
    </div>
  );
}

export function DiagnosticsImagingSection({ labResults, isLoading = false, focus }: DiagnosticsImagingSectionProps) {
  // Opens on the Imaging tab when navigated here with that request (e.g. a
  // just-finished multi-series upload — see imagingNavigation.openImagingTab).
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<Tab>(() =>
    (location.state as DiagnosticsLocationState | null)?.tab === "imaging" ? "imaging" : "labs",
  );
  // One shared value instead of two independent booleans/strings — the filter
  // pills ("All", each category/type, "Abnormal/Critical Only") render as a
  // single row and are meant to be mutually exclusive, but used to be backed
  // by filterCategory (string | null) and filterFlaggedOnly (boolean) as two
  // separate pieces of state. "All"'s active style only ever checked
  // !filterCategory, so it stayed lit up even while filterFlaggedOnly was
  // true — clicking "Critical Only" never touched filterCategory, so both
  // pills showed active at once. A single value makes "select one" the only
  // representable state.
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const filterCategory = activeFilter === "all" || activeFilter === "flagged" ? null : activeFilter;
  const filterFlaggedOnly = activeFilter === "flagged";
  const [selectedLabId, setSelectedLabId] = useState<string | null>(labResults[0]?.id ?? null);
  // A header search result: the Lab Results tab, every result listed, that one selected.
  useRevealSearchTarget(focus, (id) => labResults.some((r) => r.id === id), (id) => {
    setActiveTab("labs");
    setActiveFilter("all");
    setSelectedLabId(id);
  });

  // Patient-uploaded DICOM studies — sourced from ImagingStudiesContext
  // (mounted once in PatientProtectedLayout.tsx) so this data survives
  // navigating away from and back to this page. Uploading now happens from
  // the Upload Records page (see UploadRecords.tsx) — this page is
  // browse/view-only, just the list and the viewer.
  //
  // Deliberately NOT reading `activeBatches` here: this tab is explicitly
  // browse/view-only (no upload trigger of its own, per this section's own
  // docstring below), and ImagingStudyList's row shape is built entirely
  // around a COMPLETED series' known metadata (modality, file/frame counts)
  // that an in-flight batch simply doesn't have yet — there's no natural
  // row to show here without inventing a second, differently-shaped item
  // type for a list that's otherwise uniform. The in-flight state surfaces
  // instead on the Upload Records page (RecentDicomUploadsCard), which is
  // where the patient actually is at the moment a batch gets submitted and
  // where the old in-modal progress UI's replacement belongs.
  const {
    series: uploadedSeries,
    isLoading: studiesLoading,
    isLoadingMore: studiesLoadingMore,
    hasMore: studiesHasMore,
    error: studiesError,
    totalCount: studiesTotalCount,
    loadMore: loadMoreStudies,
  } = useImagingStudies();
  const navigate = useNavigate();
  // Navigates to the DICOM viewer page (see router.tsx and
  // DicomViewerPage.tsx) instead of opening a modal — passes the
  // already-fetched series via router state so the page can render
  // immediately instead of waiting on a redundant fetch-by-id. The route
  // param itself is the series' first member's id (see
  // RecentImagingUploadsCard's own openViewer for why).
  const openViewer = (series: ImagingSeries) => openSeriesViewer(navigate, series);

  const totalLabTests = labResults.length;
  const abnormalLabTests = labResults.filter(r => r.results.some(res => isAbnormalLabStatus(res.status))).length;
  const criticalFlags = labResults.filter(r => r.criticalFlags).length;

  const categories = useMemo(() => [...new Set(labResults.map(r => r.category))], [labResults]);

  const filteredLabResults = labResults.filter(result => {
    const matchesCategory = !filterCategory || result.category === filterCategory;
    const matchesFlagged = !filterFlaggedOnly || result.results.some(r => isAbnormalLabStatus(r.status));
    return matchesCategory && matchesFlagged;
  });

  const selectedLabResult = filteredLabResults.find(r => r.id === selectedLabId) ?? filteredLabResults[0] ?? null;

  // Stacked (below lg) the result sits under the list: bring it into view when one is picked.
  const detailRef = useRef<HTMLDivElement>(null);
  const selectLab = (id: string) => {
    setSelectedLabId(id);
    const stacked = typeof window.matchMedia === "function" && !window.matchMedia(LG_QUERY).matches;
    if (stacked) window.setTimeout(() => detailRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }), 0);
  };

  // Whether the Lab Results tab's underlying dataset has anything in it at
  // all — as opposed to the current filter simply matching nothing. The
  // filter pills are meaningless with nothing to filter, so they're hidden
  // entirely in that case instead of sitting above an empty list. The
  // Imaging tab has no equivalent: the upload control is always shown
  // regardless of whether any studies exist yet.
  const hasAnyLabData = totalLabTests > 0;

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setActiveFilter("all");
  };

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-5 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="min-h-[110px] rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm">
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
        <div className="grid items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]">
          {/* Sidebar — mirrors the real layout below (the filter dropdown, then
              rows) instead of skipping straight to the rows, so nothing pops
              in abruptly once loading finishes. */}
          <div className="min-w-0">
            <div className="mb-4">
              <Skeleton.Input active block size="default" />
            </div>
            <div className="grid gap-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
                  <Skeleton active title={{ width: "70%" }} paragraph={{ rows: 1, width: "40%" }} />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
            <Skeleton active paragraph={{ rows: 6 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      {/* Stat cards */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-5 xl:grid-cols-4">
        <StatCard icon="lucide:test-tube" label="Lab Results" value={totalLabTests} onClick={() => handleTabChange("labs")} />
        <StatCard icon="lucide:archive-x" label="Abnormal Labs" value={abnormalLabTests} onClick={() => handleTabChange("labs")} />
        <StatCard icon="lucide:scan-line" label="Imaging Studies" value={studiesTotalCount} onClick={() => handleTabChange("imaging")} />
        <StatCard icon="lucide:flag" label="Critical Labs" value={criticalFlags} onClick={() => handleTabChange("labs")} />
      </div>

      {/* Tab toggle */}
      <div className="mb-6 flex justify-center">
        <ThemedSegmented
          selectedBg="#180047"
          itemColor="#180047"
          value={activeTab}
          onChange={(v) => handleTabChange(v as Tab)}
          options={[
            { key: "labs" as const, icon: "lucide:test-tube", label: "Lab Results" },
            { key: "imaging" as const, icon: "lucide:scan-line", label: "Imaging" },
          ].map(({ key, icon, label }) => ({
            value: key,
            label: (
              <span className="flex items-center gap-2 px-5 py-1 text-sm font-extrabold">
                <Icon icon={icon} className="h-4 w-4" />
                {label}
              </span>
            ),
          }))}
          className="!rounded-full !border !border-[#180047]/30 !shadow-sm [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-thumb]:!rounded-full"
        />
      </div>

      {activeTab === "labs" ? (
        /* Nothing to show at all — one full-width empty state instead of the
           split view. With no data, a search box and filter pills have
           nothing to act on, and a second "select an item" card next to an
           already-empty list is redundant, so both are skipped. */
        !hasAnyLabData ? (
          <EmptyState
            icon={<Icon icon="lucide:test-tube" className="h-8 w-8 text-[#9CA3AF]" />}
            title="No lab results yet"
            description="Your lab results will appear here once they're available."
            className="py-24"
          />
        ) : (
          /* Split view */
          <div className="grid items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)]">
            {/* Sidebar — min-w-0 stops this grid item (and the single-column list
                grid inside it) from growing past its 340px track when a row's
                content is wide, which otherwise overflows into the detail panel
                next to it instead of respecting the split. */}
            <div className="min-w-0">
              {/* One dropdown, like Care & Treatment's year filter: All by default,
                  each category and Abnormal with its count. */}
              <div className="mb-4">
                <Select
                  id="lab-result-filter"
                  aria-label="Lab result filter"
                  className="w-full"
                  value={activeFilter}
                  onChange={(value) => setActiveFilter(value as string)}
                  options={[
                    { value: "all", label: `All (${labResults.length})` },
                    ...categories.map((c) => ({
                      value: c, label: `${categoryLabels[c]} (${labResults.filter((r) => r.category === c).length})`,
                    })),
                    { value: "flagged", label: `Abnormal (${abnormalLabTests})` },
                  ]}
                />
              </div>

              {/* Side by side (lg+): the list scrolls on its own, so the result beside it stays in view. */}
              <div className="-mx-1 grid grid-cols-[minmax(0,1fr)] gap-3 px-1 py-1 lg:max-h-[calc(100vh-11rem)] lg:overflow-y-auto">
                {filteredLabResults.length === 0 ? (
                  <EmptyListState icon="lucide:test-tube" message="No lab results match this filter" />
                ) : (
                  filteredLabResults.map((r) => (
                    <div key={r.id} data-search-id={r.id} className="min-w-0">
                      <LabRow result={r} isSelected={r.id === selectedLabResult?.id} onSelect={() => selectLab(r.id)} />
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Detail panel. Side by side it sticks and scrolls inside, so it stays in
                view beside the list. Stacked (phone) it is never sticky: a sticky panel
                in one column slid over the list. Nothing renders when the filter
                matches nothing (the list's own message says so). */}
            <div ref={detailRef} className="min-w-0 scroll-mt-4 lg:sticky lg:top-5 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              {selectedLabResult
                ? <LabResultDetail key={selectedLabResult.id} result={selectedLabResult} />
                : (filteredLabResults.length > 0 && <EmptyListState icon="lucide:test-tube" message="Select a lab result to view details" />)}
            </div>
          </div>
        )
      ) : (
        /* Imaging tab — entirely patient-uploaded DICOM studies now (no EHR/
           data-feed imaging is surfaced here any more). Browse/view only —
           uploading happens from the Upload Records page (see
           UploadRecords.tsx), not here. */
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
          <h3 className="text-sm font-extrabold text-[#0A2947]">Imaging</h3>
          <div className="mt-4">
            {studiesLoading ? (
              <div className="grid gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
                    <Skeleton active title={{ width: "50%" }} paragraph={{ rows: 1, width: "30%" }} />
                  </div>
                ))}
              </div>
            ) : studiesError ? (
              <EmptyState
                icon={<Icon icon="lucide:wifi-off" className="h-8 w-8 text-[#9CA3AF]" />}
                title="Couldn't load your imaging studies"
                description={studiesError}
                className="py-16"
              />
            ) : uploadedSeries.length === 0 ? (
              <EmptyState
                icon={<Icon icon="lucide:scan-line" className="h-8 w-8 text-[#9CA3AF]" />}
                title="No images found to show"
                description="Upload a DICOM file from the Upload Records page to see it here."
                className="py-16"
                action={
                  <Button
                    onClick={() => navigate("/patient/upload")}
                    className="!h-auto !rounded-xl !bg-[#E4002B]/50 !px-5 !py-2.5 !text-sm !font-extrabold !text-white hover:!bg-[#E4002B]/60"
                  >
                    Go to Upload Records
                  </Button>
                }
              />
            ) : (
              <ImagingStudyList
                series={uploadedSeries}
                onSelect={openViewer}
                hasMore={studiesHasMore}
                isLoadingMore={studiesLoadingMore}
                onLoadMore={loadMoreStudies}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
